import { useCallback, useEffect, useRef, useState } from "react";
import { useAbortController } from "../../../../../common/hooks/useAbortController";
import { PHYSICAL_DR_CONTENT } from "../content";
import {
  logPhysicalDrFailure,
  PRINT_PHYSICAL_DR_FAILED_MESSAGE,
} from "../utils/physicalDrLogging";
import { printPdfBlobUrl } from "../utils/printPdfBlobUrl";
import { getPhysicalDrBlobUrl } from "../utils/getPhysicalDrBlobUrl";

export type UsePhysicalDrPreviewOptions = {
  onFailure?: () => void;
};

interface PreviewState {
  isGenerating: boolean;
}

const isAbortError = (error: any): boolean =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

const idleState: PreviewState = {
  isGenerating: false,
};

const ABORT_KEY = "PhysicalDrPrint";

export function usePhysicalDrPreview(options?: UsePhysicalDrPreviewOptions) {
  const { getNewAbortSignal, isMountedRef } = useAbortController();
  const onFailureRef = useRef(options?.onFailure);
  onFailureRef.current = options?.onFailure;

  const objectUrlRef = useRef<string | null>(null);
  /** Avoid revoking the blob on unmount while print may still be using it (user navigated away mid-dialog). */
  const printSessionActiveRef = useRef(false);
  const [state, setState] = useState<PreviewState>(idleState);

  const revokeIfCurrent = useCallback((url: string) => {
    if (objectUrlRef.current === url) {
      URL.revokeObjectURL(url);
      objectUrlRef.current = null;
    }
  }, []);

  const startPreview = useCallback(
    async (terminalCode: string, routeIds: string[]) => {
      const signal = getNewAbortSignal(ABORT_KEY);

      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }

      setState({
        isGenerating: true,
      });

      try {
        const blobUrl = await getPhysicalDrBlobUrl({
          terminalCode,
          routeIds,
          signal,
        });
        if (!isMountedRef.current) {
          return;
        }
        if (signal.aborted) {
          setState(idleState);
          return;
        }
        objectUrlRef.current = blobUrl;

        if (!isMountedRef.current) {
          revokeIfCurrent(blobUrl);
          return;
        }
        if (signal.aborted) {
          revokeIfCurrent(blobUrl);
          setState(idleState);
          return;
        }

        setState(idleState);

        printSessionActiveRef.current = true;
        printPdfBlobUrl(blobUrl, () => {
          printSessionActiveRef.current = false;
          revokeIfCurrent(blobUrl);
        });
      } catch (error: any) {
        if (!isMountedRef.current) {
          return;
        }
        if (isAbortError(error)) {
          setState(idleState);
          return;
        }
        if (error?.message !== PRINT_PHYSICAL_DR_FAILED_MESSAGE) {
          logPhysicalDrFailure(PHYSICAL_DR_CONTENT.logging.printPipelineFailed, error);
        }
        onFailureRef.current?.();
        setState(idleState);
      }
    },
    [getNewAbortSignal, isMountedRef, revokeIfCurrent]
  );

  useEffect(() => {
    return () => {
      if (printSessionActiveRef.current) {
        return;
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  return {
    isGenerating: state.isGenerating,
    startPreview,
  };
}
